// src/components/map/hooks/adapters/useTimelineAdapter.ts
//
// Adapter hook extracted from Timeline.tsx.
// Encapsulates all imperative Mapbox interactions (source/layer lifecycle,
// filter updates, bubble GeoJSON updates, well-layer visibility toggling),
// playback interval management, and React Query data-fetching orchestration.
//
// The consuming component is responsible only for rendering playback controls,
// the slider, and the settings popover.
//
// Critical fix: module-level mutable variables `currentTimelineIndex` and
// `playInterval` from the original component have been replaced with `useRef`
// to prevent cross-instance contamination during hot reload or if multiple
// timelines are ever mounted concurrently.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { featureCollection } from "@turf/helpers";
import { WELL_LAYER_HIGHLIGHTED } from "constants/mapLayers.constants";
import mapboxgl, { AnySourceData, Map } from "mapbox-gl";
import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";

import {
  InitializeTimelineResultModel,
  useInitializeTimeline,
  useTimeline
} from "api/useTimeline";

import { absolutizeServiceRoot } from "components/map/utils/absolutizeServiceRoot";

import { buildBubbleFeatures } from "../../utils/timelineBubbles";

// ── Constants ────────────────────────────────────────────────────────────────

const mapServiceEndpoint = absolutizeServiceRoot(process.env.REACT_APP_MAP_SERVICE);

/** Interval in milliseconds between playback index increments. */
const PLAYBACK_INTERVAL_MS = 500;

// ── Types ────────────────────────────────────────────────────────────────────

export type Mode = "Play" | "Paused";

export type ProductKey = "BOE" | "Oil" | "Gas";

export interface TimelineAdapterState {
  /** Current playback index. */
  readonly currentIndex: number;

  /** Max bubble value for scaling. */
  readonly maxValue: number;

  /** Max bubble radius in meters. */
  readonly maxRadius: number;

  /** Selected product key. */
  readonly product: ProductKey;

  /** Timeline model returned from initialization API. */
  readonly model: Maybe<InitializeTimelineResultModel>;

  /** Current playback mode. */
  readonly mode: Mode;

  /** Whether the initialization mutation is loading. */
  readonly isInitializing: boolean;

  /** Whether the initialization mutation errored. */
  readonly isError: boolean;
}

export interface TimelineAdapterActions {
  /** Start or restart playback with the given product (triggers initialization mutation). */
  readonly play: (product: ProductKey) => void;

  /** Pause playback. */
  readonly pause: () => void;

  /** Set the current playback index (from slider). */
  readonly setCurrentIndex: (index: number) => void;

  /** Update max value (from settings). */
  readonly setMaxValue: (value: number) => void;

  /** Update max radius (from settings). */
  readonly setMaxRadius: (radius: number) => void;

  /** Update product key (from settings). */
  readonly setProduct: (product: ProductKey) => void;

  /** Clean up layers and close the timeline. */
  readonly close: () => void;
}

export type UseTimelineAdapter = TimelineAdapterState & TimelineAdapterActions;

// ── Layer IDs / Source IDs ───────────────────────────────────────────────────

const TIMELINE_LINE_LAYER = "timeline";
const TIMELINE_BUBBLE_LAYER = "timeline.bubble";
const TIMELINE_VECTOR_SOURCE = "timeline-source";
const TIMELINE_BUBBLE_SOURCE = "timeline.bubble";
const WELL_LAYER_ID = "Wells";
const WELL_LABEL_LAYER_ID = "Wells Label";

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Adapter hook that manages the timeline visualization on the Mapbox map.
 *
 * Responsibilities:
 * - Reads `filterId` (Redux) and `txnId` (Zustand) for initialization.
 * - Orchestrates `useInitializeTimeline` and `useTimeline` React Query hooks.
 * - Creates and tears down vector-tile and GeoJSON sources/layers.
 * - Manages playback interval (play/pause) with proper ref-based state.
 * - Updates map filters and bubble GeoJSON when scene data arrives.
 * - Toggles well layer visibility during timeline display.
 * - Cleans up all resources on close or unmount.
 *
 * @param map   The Mapbox GL map instance.
 * @param onClose Callback invoked after cleanup when the user closes the timeline.
 */
export function useTimelineAdapter(map: Map, onClose: () => void): UseTimelineAdapter {
  // ── Store reads ──────────────────────────────────────────────────────

  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const txnId = useMapStore(selectTxnId);

  // ── Local state ──────────────────────────────────────────────────────

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>(500);
  const [maxRadius, setMaxRadius] = useState<number>(800);
  const [product, setProduct] = useState<ProductKey>("BOE");
  const [model, setModel] = useState<Maybe<InitializeTimelineResultModel>>(undefined);
  const [mode, setMode] = useState<Mode>("Paused");

  // ── Refs (replaces module-level mutable variables) ───────────────────
  //
  // The original component stored `currentTimelineIndex` and `playInterval`
  // at module scope, causing shared mutable state across hot-reload cycles
  // and potential multi-instance bugs.  Refs scope them to the hook instance.

  const currentTimelineIndexRef = useRef(0);
  const playIntervalRef = useRef<Maybe<ReturnType<typeof setInterval>>>(undefined);

  // ── Well-layer visibility ────────────────────────────────────────────

  const hideWellLayer = useCallback(() => {
    if (!map) return;
    const wellLayer = map.getLayer(WELL_LAYER_ID);
    if (wellLayer) {
      map.setLayoutProperty(WELL_LAYER_ID, "visibility", "none");
      map.setLayoutProperty(WELL_LABEL_LAYER_ID, "visibility", "none");
      // A hovered series' paths would otherwise show outside the current scene.
      if (map.getLayer(WELL_LAYER_HIGHLIGHTED)) {
        map.setLayoutProperty(WELL_LAYER_HIGHLIGHTED, "visibility", "none");
      }
    }
  }, [map]);

  const showWellLayer = useCallback(() => {
    if (!map) return;
    const wellLayer = map.getLayer(WELL_LAYER_ID);
    if (wellLayer) {
      map.setLayoutProperty(WELL_LAYER_ID, "visibility", "visible");
      map.setLayoutProperty(WELL_LABEL_LAYER_ID, "visibility", "visible");
      if (map.getLayer(WELL_LAYER_HIGHLIGHTED)) {
        map.setLayoutProperty(WELL_LAYER_HIGHLIGHTED, "visibility", "visible");
      }
    }
  }, [map]);

  // ── Layer cleanup ────────────────────────────────────────────────────

  const clearLayer = useCallback(() => {
    if (!map) return;
    try {
      if (map.getLayer(TIMELINE_LINE_LAYER)) {
        map.removeLayer(TIMELINE_LINE_LAYER);
      }
      if (map.getLayer(TIMELINE_BUBBLE_LAYER)) {
        map.removeLayer(TIMELINE_BUBBLE_LAYER);
      }
      if (map.getSource(TIMELINE_VECTOR_SOURCE)) {
        map.removeSource(TIMELINE_VECTOR_SOURCE);
      }
      if (map.getSource(TIMELINE_BUBBLE_SOURCE)) {
        map.removeSource(TIMELINE_BUBBLE_SOURCE);
      }
      // eslint-disable-next-line no-empty
    } catch (_err) {
      // Source/layer may already have been removed by a prior cleanup path.
    }
  }, [map]);

  // ── Playback control ─────────────────────────────────────────────────

  const pause = useCallback(() => {
    if (playIntervalRef.current !== undefined) {
      clearInterval(playIntervalRef.current);
      playIntervalRef.current = undefined;
    }
    setMode("Paused");
  }, []);

  const startPlayback = useCallback(
    (timelineModel: InitializeTimelineResultModel) => {
      // If we've gone past the end, wrap around to the start.
      if (currentTimelineIndexRef.current > timelineModel.maxIndex) {
        setCurrentIndex(0);
        currentTimelineIndexRef.current = 0;
      }

      setMode("Play");

      playIntervalRef.current = setInterval(() => {
        if (currentTimelineIndexRef.current > timelineModel.maxIndex) {
          pause();
          return;
        }
        currentTimelineIndexRef.current += 1;
        setCurrentIndex(currentTimelineIndexRef.current);
      }, PLAYBACK_INTERVAL_MS);
    },
    [pause]
  );

  // ── Initialization callback (mutation onSuccess) ─────────────────────

  const onInitialized = useCallback(
    (initModel: InitializeTimelineResultModel) => {
      if (!initModel || !map) return;

      setModel(initModel);
      setMaxValue(initModel.maxCum);
      hideWellLayer();
      clearLayer();

      // --- Vector tile source + line layer (well paths) ---
      const source: AnySourceData = {
        type: "vector",
        tiles: [
          `${mapServiceEndpoint}/timeline/{z}/{x}/{y}?timelineId=${initModel.timelineId}`
        ]
      };
      map.addSource(TIMELINE_VECTOR_SOURCE, source);

      map.addLayer({
        id: TIMELINE_LINE_LAYER,
        type: "line",
        source: TIMELINE_VECTOR_SOURCE,
        "source-layer": TIMELINE_LINE_LAYER,
        paint: {
          "line-color": ["get", "color"],
          "line-width": 2
        }
      });

      // --- GeoJSON source + fill layer (bubbles) ---
      map.addSource(TIMELINE_BUBBLE_SOURCE, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      map.addLayer({
        id: TIMELINE_BUBBLE_LAYER,
        type: "fill",
        source: TIMELINE_BUBBLE_SOURCE,
        paint: {
          "fill-color": ["get", "color"],
          "fill-opacity": 0.5
        }
      });

      // Start with an empty filter — data effect will populate it.
      map.setFilter(TIMELINE_LINE_LAYER, ["in", ["get", "uwi"], ["literal", []]]);

      startPlayback(initModel);
    },
    [map, hideWellLayer, clearLayer, startPlayback]
  );

  // ── React Query hooks ────────────────────────────────────────────────

  const { mutation } = useInitializeTimeline(onInitialized);

  const { data, refetch } = useTimeline(
    model?.timelineId,
    currentIndex,
    maxValue,
    maxRadius
  );

  // ── Effects ──────────────────────────────────────────────────────────

  // Sync ref with React state and trigger a refetch whenever the index advances.
  useEffect(() => {
    refetch();
    currentTimelineIndexRef.current = currentIndex;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Update the map filter and bubble GeoJSON whenever new scene data arrives.
  useEffect(() => {
    if (!map || !data || !map.getLayer(TIMELINE_LINE_LAYER)) return;

    map.setFilter(TIMELINE_LINE_LAYER, [
      "has",
      ["get", "Uwi"],
      ["literal", data.uwisInScene]
    ]);

    const features = buildBubbleFeatures(data.bubbleSize);
    const source = map.getSource(TIMELINE_BUBBLE_SOURCE) as mapboxgl.GeoJSONSource;
    if (!source) return;

    source.setData(featureCollection(features));
  }, [data, map]);

  // Clean up the playback interval on unmount to prevent leaked timers.
  useEffect(() => {
    return () => {
      if (playIntervalRef.current !== undefined) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = undefined;
      }
    };
  }, []);

  // ── Public actions ───────────────────────────────────────────────────

  const play = useCallback(
    (productKey: ProductKey) => {
      mutation.mutate({
        filterId: filterId?.id,
        txnId: txnId?.id,
        product: productKey
      });
    },
    [mutation, filterId, txnId]
  );

  const close = useCallback(() => {
    pause();
    clearLayer();
    showWellLayer();
    onClose?.();
  }, [pause, clearLayer, showWellLayer, onClose]);

  // ── Return ───────────────────────────────────────────────────────────

  return {
    // State
    currentIndex,
    maxValue,
    maxRadius,
    product,
    model,
    mode,
    isInitializing: mutation.isLoading,
    isError: mutation.isError,

    // Actions
    play,
    pause,
    setCurrentIndex,
    setMaxValue,
    setMaxRadius,
    setProduct,
    close
  };
}
